// react_frontend/src/Components/Modules/MasterAdminDashboard.js
import React, { useEffect, useState, useContext } from "react";
import Breadcrumb from "../Partials/Breadcrumb";
import { AxiosAuthInstance } from "../../AxiosInterceptors";
import { MotherContext } from "../../Context/MotherContext";
import { useNavigate } from "react-router-dom";

// Material-UI
import {
  Box, Typography, Paper, Button, Chip, Skeleton,
  Stack, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Charts
import {
  BarChart, Bar, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, Area, ComposedChart,
} from "recharts";

// Icons (✅ FaBalanceScale & FaExclamationTriangle যুক্ত করা হয়েছে)
import {
  FaChartLine, FaChartBar, FaChartPie, FaMoneyBillWave,
  FaUsers, FaCalendarAlt, FaClock, FaMoneyCheckAlt,
  FaArrowUp, FaArrowDown, FaShoppingCart, FaUserTie,
  FaFileInvoice, FaPlus, FaEye, FaBalanceScale, FaExclamationTriangle,
} from "react-icons/fa";

// ============ Styled Components ============
const MainContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: "#f8fafc",
  minHeight: "100vh",
}));

const KPICard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  backgroundColor: "white",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: "#cbd5e1",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    transform: "translateY(-2px)",
  },
}));

const ChartCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  backgroundColor: "white",
}));

const QuickActionButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5, 3),
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
  },
}));

// ============ Badge tone styles ============
const TONES = {
  up: { bgcolor: "#ecfdf5", color: "#059669" },
  down: { bgcolor: "#fef2f2", color: "#dc2626" },
  neutral: { bgcolor: "#f1f5f9", color: "#475569" },
};

// ============ KPI Card ============
const KPICardComponent = ({ icon: Icon, title, value, badge, color, currency, format }) => (
  <KPICard>
    <Stack spacing={2}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box
          sx={{
            width: 48, height: 48, borderRadius: 2,
            bgcolor: `${color}14`, color: color,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Icon size={22} />
        </Box>
        {badge && (
          <Chip
            icon={badge.tone === "up" ? <FaArrowUp size={10} /> : badge.tone === "down" ? <FaArrowDown size={10} /> : undefined}
            label={badge.label}
            size="small"
            sx={{
              bgcolor: TONES[badge.tone]?.bgcolor || TONES.neutral.bgcolor,
              color: TONES[badge.tone]?.color || TONES.neutral.color,
              fontWeight: 700,
              "& .MuiChip-icon": { color: TONES[badge.tone]?.color || TONES.neutral.color },
            }}
          />
        )}
      </Box>
      <Box>
        <Typography variant="body2" color="#64748b" fontWeight={500} gutterBottom>
          {title}
        </Typography>
        <Typography variant="h5" fontWeight={800} color="#1e293b">
          {currency ? format(value) : Number(value || 0).toLocaleString()}
        </Typography>
      </Box>
    </Stack>
  </KPICard>
);

const SectionTitle = ({ icon: Icon, children, action }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 36, height: 36, borderRadius: 1.5,
          bgcolor: "#eef2ff", color: "#6366f1",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <Icon size={16} />
      </Box>
      <Typography variant="h6" fontWeight={700} color="#1e293b">
        {children}
      </Typography>
    </Box>
    {action}
  </Box>
);

// ============ Main Component ============
const MasterAdminDashboard = () => {
  const { company } = useContext(MotherContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("today");
  const [data, setData] = useState({ today: {}, month: {}, year: {} });
  const [loading, setLoading] = useState(false);
  const [dailyData, setDailyData] = useState([]);
  const [prevSales, setPrevSales] = useState(null); 
  
  // ✅ NEW: Accounting & Inventory States
  const [accountingSummary, setAccountingSummary] = useState({ totalAssets: 0, totalLiabilities: 0 });
  const [lowStockAlert, setLowStockAlert] = useState([]);

  const role = localStorage.getItem("who_am_i");
  const title = role === "1" ? "Master Admin Dashboard"
    : role === "2" ? "Admin Dashboard"
    : role === "3" ? "Account Dashboard"
    : "Sales Manager Dashboard";

  const COLORS = ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4"];

  const formatCurrency = (value) => {
    if (value == null) value = 0;
    const num = parseFloat(value);
    if (company.currency === "BDT") {
      return `৳${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: company.currency || "BDT",
      minimumFractionDigits: 2,
    }).format(num);
  };

  const toISO = (d) => d.toISOString().split("T")[0];

  // ✅ UPDATED: Fetch dashboard summary + Accounting + Inventory
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [dashboardRes, accountingRes, inventoryRes] = await Promise.all([
          AxiosAuthInstance.post("reports/dashboard"),
          // Fallback added so dashboard doesn't break if accounting route is pending
          AxiosAuthInstance.get("accounting/reports/trial-balance").catch(() => ({ data: { totals: {} } })),
          AxiosAuthInstance.get("inventories").catch(() => ({ data: { transactions: [] } }))
        ]);
        
        const res = dashboardRes.data;
        setData({
          today: res.today || {},
          month: res.month || {},
          year: res.year || {},
        });

        // ✅ NEW: Accounting Summary
        setAccountingSummary({
          totalAssets: accountingRes.data?.totals?.debit || 0,
          totalLiabilities: accountingRes.data?.totals?.credit || 0,
        });

        // ✅ NEW: Low Stock Alert (items with available_stock < 10)
        const allItems = inventoryRes.data?.transactions || [];
        const lowStockItems = allItems.filter(item => (item.available_stock || item.quantity || 0) < 10);
        setLowStockAlert(lowStockItems);

      } catch (error) {
        console.error("Dashboard data fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // 🔥 Fetch PREVIOUS period sales (yesterday / last month / last year)
  useEffect(() => {
    const fetchPrev = async () => {
      setPrevSales(null);
      const now = new Date();
      let range = [];
      let monthly = false;

      if (activeTab === "today") {
        const y = new Date(now);
        y.setDate(now.getDate() - 1);
        const s = toISO(y);
        range = [s, s];
      } else if (activeTab === "month") {
        range = [
          toISO(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
          toISO(new Date(now.getFullYear(), now.getMonth(), 0)),
        ];
      } else {
        range = [
          toISO(new Date(now.getFullYear() - 1, 0, 1)),
          toISO(new Date(now.getFullYear() - 1, 11, 31)),
        ];
        monthly = true;
      }

      try {
        const response = await AxiosAuthInstance.post("reports", {
          daterange: range,
          ...(monthly && { aggregate: "monthly" }),
        });
        const arr =
          (Array.isArray(response.data?.daily_breakdown) && response.data.daily_breakdown) ||
          (Array.isArray(response.data?.monthly_breakdown) && response.data.monthly_breakdown) ||
          [];
        setPrevSales(arr.reduce((sum, i) => sum + (parseFloat(i.sell) || 0), 0));
      } catch (err) {
        console.warn("Previous period fetch failed:", err);
        setPrevSales(null);
      }
    };
    fetchPrev();
  }, [activeTab]);

  // Fetch current period breakdown for the trend chart
  useEffect(() => {
    const fetchDailyData = async () => {
      try {
        const now = new Date();
        let dateRange = [];
        let isMonthlyAggregation = false;

        if (activeTab === "today") {
          const todayStr = toISO(now);
          dateRange = [todayStr, todayStr];
        } else if (activeTab === "month") {
          dateRange = [
            toISO(new Date(now.getFullYear(), now.getMonth(), 1)),
            toISO(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
          ];
        } else {
          dateRange = [
            toISO(new Date(now.getFullYear(), 0, 1)),
            toISO(new Date(now.getFullYear(), 11, 31)),
          ];
          isMonthlyAggregation = true;
        }

        const response = await AxiosAuthInstance.post("reports", {
          daterange: dateRange,
          ...(isMonthlyAggregation && { aggregate: "monthly" }),
        });

        const reportData = response.data;
        let breakdown = [];

        if (isMonthlyAggregation && Array.isArray(reportData?.monthly_breakdown)) {
          breakdown = reportData.monthly_breakdown.map((item) => ({
            date: item.month,
            sell: parseFloat(item.sell) || 0,
            discount: parseFloat(item.discount) || 0,
          }));
        } else if (Array.isArray(reportData?.daily_breakdown)) {
          breakdown = reportData.daily_breakdown.map((day) => ({
            date: day.date,
            sell: parseFloat(day.sell) || 0,
            discount: parseFloat(day.discount) || 0,
          }));
        }

        setDailyData(breakdown);
      } catch (err) {
        console.error("Failed to fetch breakdown:", err);
      }
    };

    fetchDailyData();
  }, [activeTab]);

  const currentData = data[activeTab] || {};

  // 🔥 Live metrics (all computed from real API data)
  const sales = parseFloat(currentData.total_sells) || 0;
  const purchases = parseFloat(currentData.total_purchases) || 0;
  const salaries = parseFloat(currentData.employee_salaries) || 0;
  const customers = parseFloat(currentData.total_customers) || 0;
  const netEarnings = sales - purchases - salaries;

  const pctOf = (part, whole) => (whole > 0 ? (part / whole) * 100 : null);

  const salesChange =
    prevSales != null && prevSales > 0 ? ((sales - prevSales) / prevSales) * 100 : null;

  const prevLabel =
    activeTab === "today" ? "vs yesterday" : activeTab === "month" ? "vs last month" : "vs last year";

  // Dynamic badges for each KPI card (✅ NEW badges added)
  const badges = {
    sales:
      salesChange != null
        ? { label: `${Math.abs(salesChange).toFixed(1)}% ${prevLabel}`, tone: salesChange >= 0 ? "up" : "down" }
        : { label: prevLabel.replace("vs ", "no "), tone: "neutral" },
    purchases:
      pctOf(purchases, sales) != null
        ? { label: `${pctOf(purchases, sales).toFixed(1)}% of sales`, tone: "neutral" }
        : null,
    salaries:
      pctOf(salaries, sales) != null
        ? { label: `${pctOf(salaries, sales).toFixed(1)}% of sales`, tone: "neutral" }
        : null,
    profit:
      pctOf(netEarnings, sales) != null
        ? { label: `${pctOf(netEarnings, sales).toFixed(1)}% margin`, tone: netEarnings >= 0 ? "up" : "down" }
        : { label: netEarnings >= 0 ? "profit" : "loss", tone: netEarnings >= 0 ? "up" : "down" },
    customers:
      customers > 0
        ? { label: `${formatCurrency(sales / customers)} / customer`, tone: "neutral" }
        : null,
    // ✅ NEW BADGES
    assets: { label: "Accounting", tone: "neutral" },
    lowStock: lowStockAlert.length > 0 ? { label: `${lowStockAlert.length} items < 10`, tone: "down" } : { label: "Stock Healthy", tone: "up" },
  };

  const paymentData = Object.entries(currentData.payment_methods || {}).map(
    ([method, total]) => ({ name: method, value: parseFloat(total) || 0 })
  );

  const formatDateLabel = (dateStr) => {
    if (activeTab === "year") {
      const [year, month] = dateStr.split("-");
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return monthNames[parseInt(month) - 1];
    }
    const date = new Date(dateStr);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };

  const dailySalesData = dailyData
    .slice()
    .sort((a, b) => {
      if (activeTab === "year") return a.date.localeCompare(b.date);
      return new Date(a.date) - new Date(b.date);
    })
    .map((day) => ({
      date: formatDateLabel(day.date),
      sales: parseFloat(day.sell) || 0,
      discount: parseFloat(day.discount) || 0,
    }));

  const categoryData = Array.isArray(currentData.total_sell_by_categories)
    ? currentData.total_sell_by_categories.map((cat) => ({
        name: cat.category_name || "Unknown",
        sales: parseFloat(cat.category_wise_sell) || 0,
      }))
    : [];

  // Loading skeleton
  if (loading) {
    return (
      <MainContainer>
        <Breadcrumb title={title} />
        <Stack spacing={3}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 2 }}>
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Skeleton key={i} variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
            ))}
          </Box>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 2 }}>
            <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} />
          </Box>
        </Stack>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Breadcrumb title={title} />

      {/* Quick Actions */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} color="#64748b" gutterBottom sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
          Quick Actions
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <QuickActionButton variant="contained" startIcon={<FaPlus />} onClick={() => navigate("/add_billing")}
            sx={{ bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
            New Sale
          </QuickActionButton>
          <QuickActionButton variant="contained" startIcon={<FaUserTie />} onClick={() => navigate("/hr/employees")}
            sx={{ bgcolor: "#8b5cf6", "&:hover": { bgcolor: "#7c3aed" } }}>
            Add Employee
          </QuickActionButton>
          <QuickActionButton variant="contained" startIcon={<FaFileInvoice />} onClick={() => navigate("/reports")}
            sx={{ bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" } }}>
            Generate Report
          </QuickActionButton>
          <QuickActionButton variant="outlined" startIcon={<FaEye />} onClick={() => navigate("/activity_log")}
            sx={{ borderColor: "#cbd5e1", color: "#475569" }}>
            View Activity
          </QuickActionButton>
        </Stack>
      </Box>

      {/* Time Period Selector */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={(e, v) => v && setActiveTab(v)}
          sx={{
            bgcolor: "white", borderRadius: 2, border: "1px solid #e2e8f0",
            "& .MuiToggleButton-root": {
              textTransform: "none", fontWeight: 600, px: 3, py: 1.5,
              border: "none",
              "&.Mui-selected": { bgcolor: "#6366f1", color: "white", "&:hover": { bgcolor: "#4f46e5" } },
            },
          }}
        >
          <ToggleButton value="today"><FaClock size={12} style={{ marginRight: 6 }} />Today</ToggleButton>
          <ToggleButton value="month"><FaCalendarAlt size={12} style={{ marginRight: 6 }} />This Month</ToggleButton>
          <ToggleButton value="year"><FaCalendarAlt size={12} style={{ marginRight: 6 }} />This Year</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* 🔥 KPI Cards — Updated grid for 7 cards (4 on top, 3 on bottom on large screens) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 2, mb: 3 }}>
        <KPICardComponent icon={FaMoneyBillWave} title="Total Sales" value={sales}
          badge={badges.sales} color="#6366f1" currency format={formatCurrency} />
        <KPICardComponent icon={FaShoppingCart} title="Total Purchases" value={purchases}
          badge={badges.purchases} color="#ef4444" currency format={formatCurrency} />
        <KPICardComponent icon={FaUserTie} title="Total Salaries" value={salaries}
          badge={badges.salaries} color="#f59e0b" currency format={formatCurrency} />
        <KPICardComponent icon={FaMoneyCheckAlt} title="Net Profit" value={netEarnings}
          badge={badges.profit} color={netEarnings >= 0 ? "#10b981" : "#ef4444"} currency format={formatCurrency} />
        <KPICardComponent icon={FaUsers} title="Total Customers" value={customers}
          badge={badges.customers} color="#8b5cf6" format={formatCurrency} />
        
        {/* ✅ NEW: Accounting Summary Card */}
        <KPICardComponent icon={FaBalanceScale} title="Total Assets" value={accountingSummary.totalAssets}
          badge={badges.assets} color="#0ea5e9" currency format={formatCurrency} />
        
        {/* ✅ NEW: Low Stock Alert Card */}
        <KPICardComponent icon={FaExclamationTriangle} title="Low Stock Items" value={lowStockAlert.length}
          badge={badges.lowStock} color="#f43f5e" />
      </Box>

      {/* Sales Trend Chart */}
      <ChartCard sx={{ mb: 3 }}>
        <SectionTitle icon={FaChartLine} action={
          <Chip label={`${dailySalesData.length} records`} size="small"
            sx={{ bgcolor: "#eef2ff", color: "#6366f1", fontWeight: 600 }} />
        }>
          Sales Trend — {activeTab === "today" ? "Today" : activeTab === "month" ? "This Month" : "This Year"}
        </SectionTitle>

        {dailySalesData.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={dailySalesData}>
              <defs>
                <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#94a3b8" style={{ fontSize: 12 }} />
              <YAxis tickFormatter={(val) => formatCurrency(val)} stroke="#94a3b8" style={{ fontSize: 12 }} />
              <ReTooltip
                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
                formatter={(value, name) => [formatCurrency(value), name === "sales" ? "Sales" : "Discount"]}
              />
              <Legend wrapperStyle={{ fontSize: 13 }} />
              <Area type="monotone" dataKey="sales" stroke="#6366f1" strokeWidth={3} fill="url(#salesGradient)" name="Sales" />
              <Line type="monotone" dataKey="discount" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" name="Discount" />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <FaChartLine size={64} style={{ color: "#cbd5e1", marginBottom: 16 }} />
            <Typography variant="body1" color="#64748b">No sales data available for this period</Typography>
          </Box>
        )}
      </ChartCard>

      {/* Bottom Charts */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 2 }}>
        {/* Category-wise Sales */}
        <ChartCard>
          <SectionTitle icon={FaChartBar}>Category-wise Sales</SectionTitle>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={categoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tickFormatter={(val) => formatCurrency(val)} stroke="#94a3b8" style={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" stroke="#94a3b8" style={{ fontSize: 11 }} width={100} />
                <ReTooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
                  formatter={(value) => [formatCurrency(value), "Sales"]}
                />
                <Bar dataKey="sales" name="Sales" radius={[0, 8, 8, 0]}>
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <FaChartBar size={64} style={{ color: "#cbd5e1", marginBottom: 16 }} />
              <Typography variant="body1" color="#64748b">No category data available</Typography>
            </Box>
          )}
        </ChartCard>

        {/* Payment Methods */}
        <ChartCard>
          <SectionTitle icon={FaChartPie}>Payment Methods Distribution</SectionTitle>
          {paymentData.length > 0 ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
              <Box sx={{ width: "55%", minWidth: 220, height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={paymentData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={3}>
                      {paymentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
                      formatter={(value) => [formatCurrency(value), "Amount"]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack spacing={1.5} sx={{ flex: 1, minWidth: 180 }}>
                {paymentData.map((item, index) => (
                  <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: COLORS[index % COLORS.length], flexShrink: 0 }} />
                    <Typography variant="body2" color="#64748b" sx={{ flex: 1 }}>{item.name}</Typography>
                    <Typography variant="body2" fontWeight={700} color="#1e293b">{formatCurrency(item.value)}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <FaChartPie size={64} style={{ color: "#cbd5e1", marginBottom: 16 }} />
              <Typography variant="body1" color="#64748b">No payment data available</Typography>
            </Box>
          )}
        </ChartCard>
      </Box>
    </MainContainer>
  );
};

export default MasterAdminDashboard;